import { unminifyError } from '@sgme/stacktrace';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

export type SuccessIntlKey =
  | (IntlKey & `moreMenu.copyToClipboard${string}.success`)
  | (IntlKey & `smartQuote.quickActions.copyToClipboard.quotes.success`);
export type ErrorIntlKey =
  | (IntlKey & `moreMenu.copyToClipboard${string}.error`)
  | (IntlKey & `smartQuote.quickActions.copyToClipboard.quotes.error`);

export type CopyToClipboardHandler = {
  validate: () => boolean;
  validateClipboardItem: () => boolean;
  onSuccess: () => string;
  onError: (err: any) => void;
};

export function triggerCopyTextToClipboard(
  handler: CopyToClipboardHandler,
  text?: string,
  showToast: boolean = true,
): void {
  if (!handler.validate()) {
    return;
  }
  navigator.clipboard
    .writeText(text ?? '')
    .then(showToast ? handler.onSuccess : undefined)
    .catch(showToast ? handler.onError : undefined);
}

export function useCopyTextToClipboardHandler(
  successMessage: SuccessIntlKey = 'moreMenu.copyToClipboard.success',
  errorMessage: ErrorIntlKey = 'moreMenu.copyToClipboard.error',
): CopyToClipboardHandler {
  return useCopyToClipboardHandler(successMessage, errorMessage, 'text');
}

export function useImageToClipboardHandler(
  successMessage: SuccessIntlKey = 'moreMenu.copyToClipboard.image.success',
  errorMessage: ErrorIntlKey = 'moreMenu.copyToClipboard.image.error',
): CopyToClipboardHandler {
  return useCopyToClipboardHandler(successMessage, errorMessage, 'image');
}

function useCopyToClipboardHandler(
  successMessage: SuccessIntlKey,
  errorMessage: ErrorIntlKey,
  content: 'text' | 'image',
) {
  const notification = useUserNotification();
  const toastNotificationTitle = 'toast.copyToClipboard';

  return {
    validate: () => {
      if (!navigator.clipboard) {
        if (!window.isSecureContext) {
          getFrontLogger().error(
            `Could not copy ${content} to clipboard: navigator.clipboard is undefined. Make sure to access this web site through a secure origin. (i.e : using localhost or with a valid SSL/TSL certificate)`,
          );
        } else {
          getFrontLogger().error(`Could not copy ${content} to clipboard: navigator.clipboard is undefined.)`);
        }
        notification.error({
          title: { id: toastNotificationTitle },
          content: [{ id: errorMessage }],
        });
        return false;
      }
      return true;
    },
    validateClipboardItem: () => {
      if (!window.ClipboardItem) {
        getFrontLogger().error(`Could not copy ${content} to clipboard: ClipboardItem is undefined.)`);
        notification.error({
          title: { id: toastNotificationTitle },
          content: [{ id: errorMessage }],
        });
        return false;
      }
      return true;
    },
    onSuccess: () =>
      notification.success({
        title: { id: toastNotificationTitle },
        content: [{ id: successMessage }],
      }),
    onError: async (err: unknown) => {
      const e = await unminifyError(err);
      getFrontLogger().error(`Could not copy ${content} to clipboard: {@FrontError}`, e);
      notification.error({
        title: { id: toastNotificationTitle },
        content: [{ id: errorMessage }],
      });
    },
  };
}
